package io.horion.cv.pages.job.user

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.components.Footer
import io.horion.cv.components.JobDetailsModal
import io.horion.cv.components.Topbar
import io.horion.cv.models.ApplyJob
import io.horion.cv.models.ApplyJobStatus
import io.horion.cv.models.Job
import io.horion.cv.pages.job.company.FilterButton
import io.horion.cv.utils.ApplyJobTestData
import io.horion.cv.utils.Colors
import io.horion.cv.utils.jobsList
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@OptIn(DelicateApi::class)
@Page("/job/consulter")
@Composable
fun ConsulterJob() {
    val currentUserId = "user_004"

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val breakpoint = rememberBreakpoint()

    var allJobs by remember {
        mutableStateOf(jobsList)
    }

    var applyJobs by remember {
        mutableStateOf(ApplyJobTestData.getApplicationsByUser(currentUserId))
    }

    // État pour gérer le modal
    var selectedJob by remember { mutableStateOf<Job?>(null) }
    var showApplyModal by remember { mutableStateOf(false) }

    // PAGINATION
    var currentPage by remember { mutableStateOf(0) }
    val pageSize = 5
    val paginatedApplyJobs = applyJobs
        .drop(currentPage * pageSize)
        .take(pageSize)

    // STATISTIQUES
    val totalCandidatures = paginatedApplyJobs.size
    val candidaturesAcceptees = paginatedApplyJobs.filter { it.status == ApplyJobStatus.ACCEPTED }
    val candidaturesRejetees = paginatedApplyJobs.filter { it.status == ApplyJobStatus.REJECTED }
    val candidaturesEnAttente = paginatedApplyJobs.filter { it.status == ApplyJobStatus.PENDING }

    val mesFavoris = emptyList<ApplyJob>()
    val mesAlertes = emptyList<ApplyJob>()

    // ÉTATS POUR LES FILTRES
    var selectedFilter by remember { mutableStateOf("ALL") }

    val filteredCandidatures = when (selectedFilter) {
        "ACCEPTED" -> candidaturesAcceptees
        "REJECTED" -> candidaturesRejetees
        "PENDING" -> candidaturesEnAttente
        "FAVORIS" -> mesFavoris
        "ALERTES" -> mesAlertes
        else -> paginatedApplyJobs
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.Top,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Topbar()

            // FILTRES
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = 20.px, bottom = 20.px)
                    .styleModifier {
                        property("overflow-x", "auto")
                        property("overflow-y", "hidden")
                        property("-webkit-overflow-scrolling", "touch")
                        property("scrollbar-width", "none")
                    }
            ) {
                Row(
                    modifier = Modifier
                        .padding(leftRight = 20.px)
                        .styleModifier {
                            property("width", "max-content")
                            property("min-width", "100%")
                            property("flex-wrap", "nowrap")
                        },
                    horizontalArrangement = Arrangement.spacedBy(10.px)
                ) {
                    FilterButton(
                        label = "Toutes (${totalCandidatures})",
                        isSelected = selectedFilter == "ALL",
                        onClick = {
                            selectedFilter = "ALL"
                            currentPage = 0
                        }
                    )

                    FilterButton(
                        label = "Acceptées (${candidaturesAcceptees.size})",
                        isSelected = selectedFilter == "ACCEPTED",
                        onClick = {
                            selectedFilter = "ACCEPTED"
                            currentPage = 0
                        },
                        color = Colors.secondary
                    )

                    FilterButton(
                        label = "Rejetées (${candidaturesRejetees.size})",
                        isSelected = selectedFilter == "REJECTED",
                        onClick = {
                            selectedFilter = "REJECTED"
                            currentPage = 0
                        },
                        color = Colors.Red
                    )

                    FilterButton(
                        label = "En attente (${candidaturesEnAttente.size})",
                        isSelected = selectedFilter == "PENDING",
                        onClick = {
                            selectedFilter = "PENDING"
                            currentPage = 0
                        },
                        color = Colors.orange
                    )

                    FilterButton(
                        label = "Mes Favoris (${mesFavoris.size})",
                        isSelected = selectedFilter == "FAVORIS",
                        onClick = {
                            selectedFilter = "FAVORIS"
                            currentPage = 0
                        },
                        color = Colors.Primary
                    )

                    FilterButton(
                        label = "Mes Alertes (${mesAlertes.size})",
                        isSelected = selectedFilter == "ALERTES",
                        onClick = {
                            selectedFilter = "ALERTES"
                            currentPage = 0
                        },
                        color = Colors.Primary
                    )
                }
            }

            // CONTENU PRINCIPAL - Passez la fonction de callback
            ContentJob(
                candidatures = filteredCandidatures,
                selectedFilter = selectedFilter,
                allJobs = allJobs,
                currentPage = currentPage,
                totalPages = if (selectedFilter == "ALL") {
                    (applyJobs.size + pageSize - 1) / pageSize
                } else {
                    (filteredCandidatures.size + pageSize - 1) / pageSize
                },
                onPageChange = { currentPage = it },
                onViewDetails = { job -> selectedJob = job } // Callback pour afficher le modal
            )

            Footer()
        }

        // Afficher le modal si un job est sélectionné
        if (selectedJob != null) {
            JobDetailsModal(
                job = selectedJob!!,
                onDismiss = { selectedJob = null },
                breakpoint = breakpoint,
                isUser = true,
                onClick = { showApplyModal = true },
                isDark = isDark
            )
        }
    }
}
@Composable
fun ContentJob(
    candidatures: List<ApplyJob>,
    allJobs: List<Job>,
    selectedFilter: String,
    currentPage: Int,
    totalPages: Int,
    onPageChange: (Int) -> Unit,
    onViewDetails: (Job?) -> Unit // Ajout du callback
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 20.px, topBottom = 10.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Titre selon le filtre
        val title = when (selectedFilter) {
            "ACCEPTED" -> "Candidatures acceptées"
            "REJECTED" -> "Candidatures rejetées"
            "PENDING" -> "Candidatures en attente"
            "FAVORIS" -> "Mes offres favorites"
            "ALERTES" -> "Mes alertes emploi"
            else -> "Toutes mes candidatures"
        }

        Div(attrs = {
            style {
                property("font-size", "24px")
                property("font-weight", "bold")
                property("margin-bottom", "20px")
                property("color", "#333")
            }
        }) {
            Text(title)
        }

        // Liste des candidatures
        if (candidatures.isEmpty()) {
            Div(attrs = {
                style {
                    property("text-align", "center")
                    property("padding", "40px")
                    property("color", "#666")
                    property("font-size", "16px")
                }
            }) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Image(
                        src = "/iconelistevide.png",
                        modifier = Modifier.size(80.px),
                        description = "liste vide"
                    )
                    Div(attrs = {
                        style {
                            property("margin-top", "20px")
                            property("font-size", "18px")
                        }
                    }) {
                        Text("Aucune candidature trouvée")
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(15.px)
            ) {
                candidatures.forEach { application ->
                    val job = allJobs.find { it.id == application.job }
                    CandidatureCard(
                        application = application,
                        job = job,
                        onViewDetails = { onViewDetails(job) } // Passez le callback
                    )
                }
            }

            // Pagination
            if (totalPages > 1) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 20.px),
                    horizontalArrangement = Arrangement.Center
                ) {
                    PaginationButton(
                        label = "← Précédent",
                        enabled = currentPage > 0,
                        onClick = { if (currentPage > 0) onPageChange(currentPage - 1) }
                    )

                    Div(attrs = {
                        style {
                            property("padding", "8px 16px")
                            property("margin", "0 10px")
                        }
                    }) {
                        Text("Page ${currentPage + 1} / $totalPages")
                    }

                    PaginationButton(
                        label = "Suivant →",
                        enabled = currentPage < totalPages - 1,
                        onClick = { if (currentPage < totalPages - 1) onPageChange(currentPage + 1) }
                    )
                }
            }
        }
    }
}

@Composable
fun CandidatureCard(
    application: ApplyJob,
    job: Job?,
    onViewDetails: (Job?) -> Unit // Ajout du callback
) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "auto")
            property("background", "#ffffff")
            property("border-radius", "8px")
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            property("padding", "16px")
            property("margin-bottom", "10px")
            property("border-left", "4px solid ${getStatusColor(application.status)}")
        }
    }) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column {
                // Titre du job avec le nom de l'enterprise
                Div(attrs = {
                    style {
                        property("font-weight", "bold")
                        property("font-size", "22px")
                        property("margin-bottom", "5px")
                        property("color", "#1a1a1a")
                    }
                }) {
                    if (job != null) {
                        Text("${job.title} - ${job.enterprise}")
                    } else {
                        Text("Poste non trouvé (ID: ${application.job})")
                    }
                }

                // Entreprise
                if (job != null) {
                    Div(attrs = {
                        style {
                            property("color", "#555")
                            property("font-size", "18px")
                            property("margin-bottom", "5px")
                        }
                    }) {
                        Text(job.enterprise)
                    }
                }

                // Ville
                if (job != null) {
                    Div(attrs = {
                        style {
                            property("color", "#666")
                            property("font-size", "15px")
                            property("margin-bottom", "5px")
                        }
                    }) {
                        Text(" ${job.city}")
                    }
                }

                // Date de candidature
                Div(attrs = {
                    style {
                        property("color", "#888")
                        property("font-size", "14px")
                        property("margin-bottom", "5px")
                    }
                }) {
                    Text("Candidature du : ${formatDate(application.createdAt)}")
                }

                // Badge de statut
                Div(attrs = {
                    style {
                        property("margin-top", "10px")
                    }
                }) {
                    StatusBadge(status = application.status)
                }
            }

            // Bouton voir détails - AJOUT DE L'ONCLICK
            Div(attrs = {
                style {
                    property("cursor", "pointer")
                    property("background", Colors.Primary)
                    property("color", "#fff")
                    property("padding", "8px 16px")
                    property("border-radius", "4px")
                    property("font-size", "16px")
                    property("border", "none")
                    property("transition", "all 0.3s ease")
                }
                onClick {
                    onViewDetails(job) // Appel du callback quand on clique
                }
            }) {
                Text("Voir détails")
            }
        }
    }
}
@Composable
fun StatusBadge(status: ApplyJobStatus) {
    val (color, text) = when (status) {
        ApplyJobStatus.ACCEPTED -> Pair(Colors.secondary, "Acceptée")
        ApplyJobStatus.REJECTED -> Pair(Colors.Red, "Rejetée")
        ApplyJobStatus.PENDING -> Pair(Colors.orange, "En attente")
    }

    Div(attrs = {
        style {
            property("display", "inline-block")
            property("background", color)
            property("color", "#fff")
            property("padding", "4px 12px")
            property("border-radius", "12px")
            property("font-size", "14px")
            property("font-weight", "500")
        }
    }) {
        Text(text)
    }
}


@Composable
fun PaginationButton(label: String, enabled: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        style {
            property("cursor", if (enabled) "pointer" else "not-allowed")
            property("padding", "8px 16px")
            property("border-radius", "4px")
            property("background", if (enabled) "#474747" else "#ccc")
            property("color", "#fff")
            property("margin", "0 5px")
            property("opacity", if (enabled) "1" else "0.5")
        }
        if (enabled) {
            onClick { onClick() }
        }
    }) {
        Text(label)
    }
}

fun getStatusColor(status: ApplyJobStatus): CSSColorValue {
    return when (status) {
        ApplyJobStatus.ACCEPTED -> Colors.secondary
        ApplyJobStatus.REJECTED -> Colors.Red
        ApplyJobStatus.PENDING -> Colors.orange
    }
}

// Fonction utilitaire pour formater la date
fun formatDate(dateString: String): String {
    return try {
        val parts = dateString.split("T")
        val date = parts[0]
        val dateParts = date.split("-")
        "${dateParts[2]}/${dateParts[1]}/${dateParts[0]}"
    } catch (e: Exception) {
        dateString
    }
}